import { Children, memo, MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router";
import { Button, ButtonProps } from "../Button";
import { PageLayoutHeaderButtons } from "../Layout";
import { Link, LinkProps } from "../Link";
import { Step } from "./components/Step";
import { StepperContext } from "./StepperContext";

import styles from "./style.module.scss";

export type StepperInstance = {
  stepIndex: number,
  setStepIndex: (index: number) => void,
}

export type StepDefinition = {
  title: string
  onNext?: (setStepIndex: (v: number) => void) => boolean | Promise<boolean>
  onBack?: (setStepIndex: (v: number) => void) => boolean | Promise<boolean>
  children: ReactNode
  backButtonProps?: LinkProps & { hidden?: boolean }
  nextButtonProps?: ButtonProps & { hidden?: boolean }
  stepperRef?: MutableRefObject<StepperInstance | undefined>
}

export type StepperProps = {
  contentClassName?: string,
  steps: StepDefinition[]
  onStepChange?: (index: number, step: StepDefinition) => void
  showBackButtonOnFirstStep?: boolean,
  useURLSearchParams?: boolean
}

export const Stepper = memo<StepperProps>((
  {
    steps,
    onStepChange,
    contentClassName,
    showBackButtonOnFirstStep,
    useURLSearchParams = true,
  }) => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [ initialized, setInitialized ] = useState(false);
  const [ stepIndex, setStepIndexInternal ] = useState(0);

  const setStepIndex = useCallback((index: number) => {
    const step = Math.min(Math.max(index, 0), steps.length - 1);
    if (useURLSearchParams) {
      navigate({
        search: new URLSearchParams({ step: step.toString() }).toString(),
      });
    }
    else {
      setStepIndexInternal(step);
    }
  }, [ steps.length, navigate, useURLSearchParams ]);

  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      setStepIndex(0);
    } else if (useURLSearchParams) {
      setStepIndexInternal(Number(new URLSearchParams(search).get("step")) || 0);
    }
  }, [ initialized, setInitialized, setStepIndex, setStepIndexInternal, search, useURLSearchParams ]);

  const currentStep = steps[stepIndex];

  useEffect(() => {
    onStepChange?.(stepIndex, currentStep);
  }, [ onStepChange, stepIndex, currentStep ]);

  steps.forEach(value => {
    if (value?.stepperRef) {
      value.stepperRef.current = {
        stepIndex,
        setStepIndex,
      };
    }
  });

  const onBackClick = useCallback(async () => {
    if (!currentStep?.onBack || await currentStep?.onBack(setStepIndex)) {
      setStepIndex(stepIndex - 1);
    }
  }, [ currentStep, setStepIndex, stepIndex ]);

  const onNextClick = useCallback(async () => {
    if (!currentStep?.onNext || await currentStep?.onNext(setStepIndex)) {
      setStepIndex(stepIndex + 1);
    }
  }, [ currentStep, setStepIndex, stepIndex ]);

  return (
    <StepperContext.Provider value={useMemo(() => ({
      setNextStep: onNextClick,
      setPreviousStep: onBackClick,
    }), [ onNextClick, onBackClick ])}>
      <div className={classNames(styles.root)}>
        <div className={styles.steps}>
          {steps.map((x, i) =>
            <Step
              key={x.title}
              title={x.title}
              isActive={i === stepIndex}
              isCompleted={i < stepIndex}
            />,
          )}
        </div>

        <div className={classNames(styles.content, contentClassName)}>
          <h2 className={styles.header}>Step {stepIndex + 1}/{steps.length} {currentStep?.title}</h2>
          {currentStep?.children}
        </div>

        {
          !!steps?.length &&
          <PageLayoutHeaderButtons>
            {
              ((stepIndex !== 0 || showBackButtonOnFirstStep) && !currentStep.backButtonProps?.hidden) &&
              <Link
                component={"button"}
                {...currentStep.backButtonProps}
                className={classNames(styles.backButton, currentStep.backButtonProps?.className)}
                onClick={onBackClick}
              >
                {Children.count(currentStep.backButtonProps?.children) > 0 ? currentStep.backButtonProps?.children : "Back"}
              </Link>
            }
            {
              !currentStep.nextButtonProps?.hidden &&
              <Button
                buttonType="secondary"
                buttonColorSchema="default"
                {...currentStep.nextButtonProps}
                className={classNames(styles.nextButton, currentStep.nextButtonProps?.className)}
                onClick={onNextClick}
              >
                {Children.count(currentStep.nextButtonProps?.children) > 0 ? currentStep.nextButtonProps?.children : "Next"}
              </Button>
            }
          </PageLayoutHeaderButtons>
        }
      </div>
    </StepperContext.Provider>
  );
});

export default Stepper;
